import React from "react";
import Reveal from "./Reveal";
import FAQ from "./FAQ";

const ARENA_FAQS = [
  {
    q: "What is the AI Innovation Arena?",
    a: "The AI Innovation Arena is the global championship for AI builders. Spring regional editions run across schools, colleges, and corporates, and one global December finale crowns a champion cohort. Winners receive capital, mentorship, and GTM backing from AI For Everyone.",
  },
  {
    q: "How do colleges bring AI For Everyone to their campus?",
    a: "Colleges partner with AI For Everyone to host an Innovation Arena edition on campus, deploy the 17-module flagship programme, and route their top pods into our incubation pathway. Engineering, commerce, and arts undergraduates are all eligible.",
  },
  {
    q: "Who can apply as a builder?",
    a: "Students, professionals, and working teams from schools, colleges, and corporates in any region can apply. Pods are evaluated through a four-phase format: ideation, prototype, build, and pitch.",
  },
  {
    q: "What did the inaugural edition look like?",
    a: "The inaugural edition ran at Haldia Institute of Technology on May 8 & 16, 2026. 700+ students trained, 70 pods built real AI products, and the top 10 finalists pitched to industry CIOs. 15 winning builders are now being backed.",
  },
];

export default function InnovationArena() {
  return (
    <section id="arena" className="section-dark py-10 md:py-14" data-testid="arena-section">
      <div className="container-edge">
        <Reveal>
          <p className="section-number">
            10 — THE INNOVATION ARENA
          </p>
        </Reveal>

        <Reveal>
          <h2
            className="mt-6 font-display text-[40px] sm:text-[52px] lg:text-[68px] leading-[0.95] tracking-tight max-w-[1000px]"
            style={{ fontWeight: 500 }}
          >
            The AI Innovation Arena.
            <br />
            <em className="italic" style={{ fontWeight: 400 }}>
              The <span style={{ color: "var(--accent-gold)" }}>global</span> championship for AI builders.
            </em>
          </h2>
        </Reveal>

        <Reveal delay={140}>
          <p
            className="mt-6 max-w-[760px] text-[17px] md:text-[19px] leading-relaxed"
            style={{ color: "rgba(250,250,247,0.78)" }}
          >
            Most innovation programs end with an event. Ours ends with a company. Across spring
            regional editions and one global December finale, the best AI builders from schools,
            colleges, and corporates compete on one stage — and the winners get backed.
          </p>
        </Reveal>

        {/* Two columns */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Cadence */}
          <div className="lg:col-span-7">
            <Reveal>
              <p className="mono-label-gold mb-6">THE CADENCE</p>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-10">
              <Reveal delay={80}>
                <p className="font-mono text-[12px] mb-3" style={{ color: "var(--accent-gold)", letterSpacing: "0.16em" }}>
                  SPRING
                </p>
                <h3 className="font-display text-[28px] leading-tight" style={{ fontWeight: 500 }}>
                  Regional Editions
                </h3>
                <ul
                  className="mt-4 space-y-2 text-[14px]"
                  style={{ color: "rgba(250,250,247,0.78)" }}
                >
                  <li>March – April</li>
                  <li>Schools · Colleges · Corporates</li>
                  <li>Pod-based · 4-phase format</li>
                </ul>
              </Reveal>
              <Reveal delay={160}>
                <p className="font-mono text-[12px] mb-3" style={{ color: "var(--accent-gold)", letterSpacing: "0.16em" }}>
                  DECEMBER
                </p>
                <h3 className="font-display text-[28px] leading-tight" style={{ fontWeight: 500 }}>
                  The Global Finale
                </h3>
                <ul
                  className="mt-4 space-y-2 text-[14px]"
                  style={{ color: "rgba(250,250,247,0.78)" }}
                >
                  <li>National + international winners</li>
                  <li>One stage. One champion cohort.</li>
                  <li>Real backing. Real capital.</li>
                </ul>
              </Reveal>
            </div>

            <Reveal delay={260}>
              <p
                className="mt-10 font-mono text-[11px] leading-relaxed max-w-[520px]"
                style={{ color: "rgba(250,250,247,0.55)", letterSpacing: "0.08em" }}
              >
                Year 1 inaugural edition began at Haldia Institute of Technology, India — May 16,
                2026. Editions opening across South Asia, GCC, Southeast Asia, and Africa through
                2026–27.
              </p>
            </Reveal>
          </div>

          {/* Proof case study */}
          <div className="lg:col-span-5">
            <Reveal delay={120}>
              <div
                className="p-8 lg:p-10 border"
                style={{ borderColor: "rgba(201,169,97,0.4)", background: "rgba(20,24,50,0.5)" }}
                data-testid="hit-case-study"
              >
                <p className="mono-label-gold">INAUGURAL EDITION</p>
                <h4
                  className="mt-3 font-display text-[26px] leading-tight"
                  style={{ fontWeight: 500 }}
                >
                  Haldia Institute of Technology
                </h4>
                <p
                  className="mt-2 font-mono text-[12px]"
                  style={{ color: "rgba(250,250,247,0.6)", letterSpacing: "0.08em" }}
                >
                  MAY 8 & 16, 2026
                </p>

                <ul className="mt-6 space-y-3">
                  {[
                    ["1,000+", "attending the Grand Finale"],
                    ["700+", "students trained"],
                    ["70", "pods built real AI products"],
                    ["Top 10", "finalists pitched to CIOs"],
                    ["NASSCOM", "+ industry leaders judging"],
                  ].map(([n, l]) => (
                    <li key={l} className="flex items-baseline gap-4">
                      <span
                        className="font-display text-[22px] min-w-[88px]"
                        style={{ fontWeight: 500, color: "var(--accent-gold)" }}
                      >
                        {n}
                      </span>
                      <span className="text-[14px]" style={{ color: "rgba(250,250,247,0.85)" }}>
                        {l}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6 pt-6 border-t" style={{ borderColor: "rgba(250,250,247,0.12)" }}>
                  <p className="font-display italic text-[18px]" style={{ fontWeight: 400 }}>
                    15 winning builders now being backed.
                  </p>
                  <p
                    className="mt-1 font-mono text-[11px]"
                    style={{ color: "rgba(250,250,247,0.55)", letterSpacing: "0.08em" }}
                  >
                    CO-FUNDED BY HIT
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        {/* For Industry — talent pipeline block */}
        <Reveal delay={220}>
          <div
            className="mt-16 p-7 lg:p-9 border"
            style={{
              borderColor: "rgba(201,169,97,0.35)",
              background: "rgba(20,24,50,0.4)",
            }}
            data-testid="arena-for-industry"
          >
            <p className="mono-label-gold mb-4">FOR INDUSTRY — THE TALENT PIPELINE</p>
            <p
              className="font-display text-[20px] md:text-[24px] leading-snug max-w-[920px]"
              style={{ fontWeight: 400, color: "rgba(250,250,247,0.92)" }}
            >
              Every Arena edition produces a cohort of AI practitioners who have already built
              real products under industry-grade pressure. Companies hiring AI talent get
              first access to the top builders — through judging panels, sponsorship slots,
              recruiter access, and exclusive hiring windows before Arena winners enter the
              open marketplace.
            </p>
            <p
              className="mt-4 font-display italic text-[16px] md:text-[18px]"
              style={{ color: "var(--accent-gold)", fontWeight: 400 }}
            >
              Hire from the championship. Sponsor a regional edition. Judge the finale. Or do all three.
            </p>
          </div>
        </Reveal>

        {/* CTAs */}
        <Reveal delay={260}>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <a href="#enquiry" className="btn-gold" data-testid="arena-cta-campus">
              Bring the Arena to your campus →
            </a>
            <a
              href="#enquiry"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 text-[15px] font-medium rounded-sm transition-colors"
              style={{ color: "var(--canvas)", border: "1px solid rgba(250,250,247,0.4)" }}
              data-testid="arena-cta-company"
            >
              Bring the Arena to your company →
            </a>
            <a
              href="#enquiry"
              className="btn-gold"
              data-testid="arena-cta-hire"
              onClick={() => {
                try {
                  window.dispatchEvent(
                    new CustomEvent("enquiry:prefill", {
                      detail: { interest: "talent_hiring", audience: "corporate" },
                    })
                  );
                } catch (_) {}
              }}
            >
              Hire from the Arena →
            </a>
            <a
              href="#enquiry"
              className="inline-flex items-center justify-center text-[15px] link-underline"
              style={{ color: "rgba(250,250,247,0.85)" }}
              data-testid="arena-cta-builder"
            >
              Apply as a builder →
            </a>
          </div>
        </Reveal>

        {/* FAQ */}
        <Reveal delay={340}>
          <div className="mt-14 border-t" style={{ borderColor: "rgba(250,250,247,0.12)" }}>
            <FAQ items={ARENA_FAQS} testIdPrefix="arena-faq" theme="dark" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}
